import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';

import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { post } from '@/utils/client';
import useRouterQuery from '@/hooks/utils/useRouterQuery';

import { EmailType } from '@/types/brandedTypes/email';

//Libs
import { emailValid } from '@/lib/regex';
import { FORGOT_PASSWORD } from '@/lib/routes/apiEndpoints';

import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';

type ForgotPasswordFormType = { email: EmailType };

const ForgotPasswordForm = ({
    setErrorMessage
}: {
    setErrorMessage: Dispatch<SetStateAction<ReactNode>>;
}) => {
    const {
        register,
        setError,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm<ForgotPasswordFormType>();
    const { t: tSignin } = useTranslation('signin');
    const { t: tError } = useTranslation('errors');
    const { t_error } = useErrorTranslation();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const router = useRouter();
    const { clearUrlQueriesWithoutReload } = useRouterQuery();
    const { success, invalid_data } = router.query;

    const onSubmit = async ({ email }: ForgotPasswordFormType) => {
        setErrorMessage(null);
        const token = await executeRecaptcha('forgot_password');

        try {
            await post(FORGOT_PASSWORD, { email, token });
            setErrorMessage('success');
        } catch (e) {
            // Error code 422 = error in form => fields errors
            if (e.status === 422) {
                if (e?.response?.data?.errors?.email?.[0] === 'user') {
                    setErrorMessage('success');
                } else {
                    const errorsFields = e.response?.data?.errors as Record<
                        'token' | keyof ForgotPasswordFormType,
                        string[]
                    >;
                    // Parse error object, get first error and display errors under each field
                    if (errorsFields) {
                        Object.keys(errorsFields).forEach((field) => {
                            const error_type = errorsFields[field][0];

                            if (field === 'token') {
                                setErrorMessage(tError('error_recaptacha'));
                            } else {
                                setError(field, {
                                    type: 'manual',
                                    message: t_error('error_' + field + '_' + error_type)
                                });
                            }
                        });
                    } else {
                        setErrorMessage(tError('error_occured_submit_data'));
                    }
                }
            } else {
                setErrorMessage(tError('error_occured_submit_data'));
            }
        }
    };

    useEffect(() => {
        if (success) {
            setErrorMessage('success');
            clearUrlQueriesWithoutReload();
        } else if (invalid_data) {
            setErrorMessage(tError('error_occured_submit_data'));
            clearUrlQueriesWithoutReload();
        }
    }, []);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Label htmlFor="email" className="hidden">
                {tSignin('your_email')}
            </Label>
            <Input
                id="email"
                placeholder={tSignin('your_email')}
                type="email"
                className="mt-4"
                inputStyle="default_no_background"
                error={errors?.email?.message}
                {...register('email', {
                    required: tError('error_email_required'),
                    pattern: {
                        value: emailValid,
                        message: tError('error_email_email')
                    }
                })}
            />
            <Button
                type="submit"
                isLoading={isSubmitting}
                className="mt-10 w-full"
                id="forgot-password-submit">
                {tSignin('submit')}
            </Button>
        </form>
    );
};

export default ForgotPasswordForm;
